import ChartHelpers from "features/Charting/helpers/ChartHelpers";
import useChartOptions from "features/Charting/hooks/useChartOptions";
import useSetChartSelections from "features/Charting/hooks/useSetChartSelections";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { EntityType } from "types/entity";
import useChartMeasures from "./useChartMeasures";
import useDataVisualizationType from "./useDataVisualizationType";
import moment from "moment";
import { useTranslation } from "react-i18next";

function useChartQueryParams({ sourceEnabled }: { sourceEnabled?: boolean } = {}) {
  const { t } = useTranslation();
  const options = useChartOptions();
  const query = new URLSearchParams(useLocation().search);
  const chartMeasures = useChartMeasures();
  const visType = useDataVisualizationType();
  const setSelections = useSetChartSelections();

  const queryParams: {
    metadataId?: string;
    chartType?: string;
    interval?: string;
    sourceId?: string;
  } & { [id: string]: string } = {};

  type ValidKeys = keyof typeof queryParams;

  query.forEach((value, key: ValidKeys) => {
    queryParams[key] = value;
  });

  const changeMeasureAtIndex = chartMeasures.onChangeAtIndex;

  const measure = useMemo(() => {
    if (!queryParams.metadataId) {
      return undefined;
    }
    return ChartHelpers.filterMeasuresByDataVisType(options.measures, { visType }).find(
      (measure) => measure.id === queryParams.metadataId
    );
  }, [options.measures, queryParams.metadataId, visType]);

  const source = useMemo(() => {
    /**
     * @densk1
     * Need to confirm that athletes have loaded, because
     * if not a rerender breaks references equality on selected dropdown */
    const hasSomeAthleteSources = !!options.sources.find((source) => source.type === EntityType.ATHLETE);

    if (!(sourceEnabled && queryParams.sourceId && hasSomeAthleteSources)) {
      return;
    }

    return options.sources.find((source) => source.id === queryParams.sourceId);
  }, [options.sources, queryParams.sourceId, sourceEnabled]);

  const period = useMemo(() => {
    if (!queryParams.interval) return null;
    if (queryParams.interval === "custom") {
      return {
        period: { label: t("global.custom_label"), value: null },
        startDate: moment(queryParams.start).startOf("day"),
        endDate: moment(queryParams.end).endOf("day"),
      };
    }
  }, [t, queryParams.end, queryParams.interval, queryParams.start]);

  useEffect(() => {
    setTimeout(() => {
      measure && changeMeasureAtIndex(0)(undefined, measure);
      setSelections((prev) => ({
        ...prev,
        sources: source ? [source] : prev.sources,
        period: period ? period.period : prev.period,
        startDate: period ? period.startDate : prev.startDate,
        endDate: period ? period.endDate : prev.endDate,
      }));
    }, 100);
  }, [changeMeasureAtIndex, measure, period, setSelections, source]);
}
export default useChartQueryParams;
